import { takeLatest, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { collaboratorsGroupsApi } from 'services/apiDigitalTicket/collaboratorsGroups';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as GroupsActions from './actions';
import { GroupsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof GroupsActions.listGroupsRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando grupos' }));
    }
    const response = yield call(collaboratorsGroupsApi.get, {
      params: { ...body },
    });

    if (response.status === 200) {
      yield put(
        GroupsActions.listGroupsSuccess({
          groupsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('groups - listGroups - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.listGroupsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
}: ReturnType<typeof GroupsActions.filterGroupsRequest>): any {
  try {
    const response = yield call(collaboratorsGroupsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(
        GroupsActions.filterGroupsSuccess({
          groupsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('groups - filterGroups - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.filterGroupsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof GroupsActions.getGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(collaboratorsGroupsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.getGroupSuccess({ groupData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('groups - getGroup - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.getGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof GroupsActions.createGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando grupo' }));
    const response = yield call(collaboratorsGroupsApi.create, {
      data: payload,
    });

    if (response.status === 200) {
      yield put(GroupsActions.createGroupSuccess({ groupData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Grupo criado com sucesso',
        }),
      );
      yield put(push(PATH.COMPANY_GROUP.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('groups - createGroup- error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.createGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof GroupsActions.updateGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const response = yield call(collaboratorsGroupsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.updateGroupSuccess({ groupData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Grupo alterado com sucesso',
        }),
      );
      yield put(
        GroupsActions.listGroupsRequest({ body: { _includes: 'user' } }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('groups - updateGroup - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.updateGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof GroupsActions.deleteGroupRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo grupo' }));
    const response = yield call(collaboratorsGroupsApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(GroupsActions.deleteGroupSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Grupo excluído com sucesso',
        }),
      );
      yield put(
        GroupsActions.listGroupsRequest({ body: { _includes: 'user' } }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('groups - deleteGroup - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(GroupsActions.deleteGroupFailure());
    yield put(DialogsActions.setClose());
  }
}

export const collaboratorsGroupsSagas = [
  takeLatest(GroupsTypes.LIST_GROUPS_REQUEST, list),
  takeLatest(GroupsTypes.GET_GROUP_REQUEST, get),
  takeLatest(GroupsTypes.FILTER_GROUPS_REQUEST, filter),
  takeLatest(GroupsTypes.CREATE_GROUP_REQUEST, create),
  takeLatest(GroupsTypes.UPDATE_GROUP_REQUEST, update),
  takeLatest(GroupsTypes.DELETE_GROUP_REQUEST, remove),
];
